/* eslint-disable no-unreachable-loop */
import { FC, useEffect, useState } from 'react';
import Header from '@/components/Header/Header';
import styles from './NationalModifiers.module.css';
import TableCard from '@/components/Tables/TableCard/TableCard';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import {
  identicalArrays,
  useNationalModifiers,
  useUpdateNationalModifiersLock,
} from '@/services/nationalModifiersService';
import { NationalModifiersDetails, NationalModifiersSelection } from '@/types/nationalModifiers.types';
import { SwitchCell } from './internal/SwitchCell';
import Button from '@/components/Button/Button';
import { useTranslation } from 'react-i18next';

const NationalModifiers: FC = () => {
  const { t } = useTranslation();
  const { data: dataNationalModifiers, isLoading, isError, refetch } = useNationalModifiers(true);
  const [data, setData] = useState(dataNationalModifiers?.data || []);
  const [originalData, setOriginalData] = useState(dataNationalModifiers?.data || []);
  const [disableSaveChangesButton, setDisableSaveChangesButton] = useState(true);
  const { mutate: updateIsLockForNationalModifiers } = useUpdateNationalModifiersLock();

  const columns = [
    {
      cell: SwitchCell,
      header: t('modifiers.table.lockPrice'),
      accessorKey: 'isLock',
      meta: { disableFilter: true },
    },
    {
      accessorFn: (originalRow: NationalModifiersDetails) => originalRow.slotName || '-',
      header: t('common.slot'),
      enableSorting: true,
    },
    {
      accessorFn: (originalRow: NationalModifiersDetails) => {
        const shouldShowWeightName =
          originalRow.modifierWeightName && originalRow.modifierWeightName.toLowerCase() !== 'none';
        return `${originalRow.modifierName}${shouldShowWeightName ? `, ${originalRow.modifierWeightName}` : ''}` || '-';
      },
      header: t('modifiers.table.modifier'),
      enableSorting: true,
    },
  ];

  if (isError) {
    showToast({
      label: t('modifiers.errorFetching'),
      toastType: ToastTypes.Error,
      id: 'errorFetchingNationalModifiers',
    });
  }

  useEffect(() => {
    if (dataNationalModifiers?.data) {
      setData(dataNationalModifiers.data);
      setOriginalData(dataNationalModifiers.data);
    }
  }, [dataNationalModifiers]);

  const onButtonClick = () => {
    const nationalModifiersToUpdateSelection: NationalModifiersSelection[] = [];

    originalData.forEach((modifier) => {
      const resultObject = data.filter((modifierInternal) => modifierInternal.id === modifier.id)[0];

      if (resultObject.isLock !== modifier.isLock) {
        const nms: NationalModifiersSelection = {
          id: resultObject.id,
          isLock: resultObject.isLock,
        };
        nationalModifiersToUpdateSelection.push(nms);
      }
    });

    updateIsLockForNationalModifiers(nationalModifiersToUpdateSelection, {
      onSuccess: () => {
        showToast({
          id: 'editNationalModifiersLock',
          label: t('modifiers.lockStatusUpdated'),
        });

        refetch();
      },
      onError: () => {
        showToast({
          id: 'errorUpdatingNationalModifiersLock',
          label: t('modifiers.errorUpdatingLockStatus'),
          toastType: ToastTypes.Error,
        });
      },
    });
  };

  useEffect(() => {
    const identicalData = identicalArrays(originalData, data);
    setDisableSaveChangesButton(identicalData);
  }, [data, originalData]);

  return (
    <>
      <Header title="National Modifiers" breadcrumb={{ to: '/', label: 'Back to Home' }} />
      <div className={styles.page}>
        <div className={styles.tableLabel}>
          All Modifiers
          <Button disabled={disableSaveChangesButton} onClick={() => onButtonClick()}>
            Save Changes
          </Button>
        </div>
        <TableCard
          height="calc(75vh - 100px)"
          isLoading={isLoading}
          data={data}
          meta={{
            columns,
            data,
            updateData: (rowIndex: number, columnId: string, value: string | boolean) => {
              setData((old) =>
                old.map((row, index) => {
                  if (index === rowIndex) {
                    return {
                      ...old[rowIndex],
                      [columnId]: value,
                    };
                  }
                  return row;
                }),
              );
            },
          }}
          columns={columns}
          enableFilters
        />
      </div>
    </>
  );
};

export default NationalModifiers;
